import { asbError } from '@project/common/util/log';
import type {
    AlertMessage,
    AnkiSettingsToVideoMessage,
    AppBarToggleMessageToVideoMessage,
    AudioTrackModel,
    AudioTrackSelectedFromVideoMessage,
    AudioTrackSelectedToVideoMessage,
    CardTextFieldValues,
    CopyMessage,
    CopyToVideoMessage,
    CurrentTimeToVideoMessage,
    DurationFromVideoMessage,
    FullscreenToggleMessageToVideoMessage,
    HideSubtitlePlayerToggleToVideoMessage,
    MiscSettingsToVideoMessage,
    OffsetFromVideoMessage,
    OffsetToVideoMessage,
    PauseFromVideoMessage,
    PlaybackState,
    PlaybackStateFromVideoMessage,
    PlaybackRateFromVideoMessage,
    PlaybackRateToVideoMessage,
    PlayFromVideoMessage,
    PlayMode,
    PlayModeMessage,
    PlayModesMessage,
    PostMineAction,
    ReadyFromVideoMessage,
    ReadyStateFromVideoMessage,
    ReadyToVideoMessage,
    SubtitleModel,
    SubtitleSettingsToVideoMessage,
    SubtitlesToVideoMessage,
    SubtitlesUpdatedFromVideoMessage,
    ToggleSubtitleTrackInListFromVideoMessage,
    SubtitlesUpdatedToVideoMessage,
    SaveTokenLocalFromVideoMessage,
    SaveTokenLocalToVideoMessage,
    IndexedSubtitleModel,
} from '@project/common';
import type {
    AnkiSettings,
    ApplyStrategy,
    MiscSettings,
    SubtitleSettings,
    TokenState,
    TokenStatus,
} from '@project/common/settings';

export default class PlayerChannel {
    private channel?: BroadcastChannel;
    private readyCallbacks: ((duration: number, videoFileName?: string) => void)[];
    private playCallbacks: (() => void)[];
    private pauseCallbacks: (() => void)[];
    private playbackStateCallbacks: ((state: PlaybackStateFromVideoMessage) => void)[];
    private latestPlaybackState?: PlaybackStateFromVideoMessage;
    private currentTimeCallbacks: ((currentTime: number) => void)[];
    private audioTrackSelectedCallbacks: ((id: string) => void)[];
    private closeCallbacks: (() => void)[];
    private subtitlesCallbacks: ((subtitles: SubtitleModel[], subtitleFileName: string) => void)[];
    private subtitlesUpdatedCallbacks: ((updatedSubtitles: readonly IndexedSubtitleModel[]) => void)[];
    private saveTokenLocalCallbacks: ((
        track: number,
        token: string,
        status: TokenStatus | null,
        states: TokenState[],
        applyStates: ApplyStrategy
    ) => void)[];
    private offsetCallbacks: ((offset: number) => void)[];
    private playbackRateCallbacks: ((playbackRate: number) => void)[];
    private playModeCallbacks: ((playMode: PlayMode) => void)[];
    private hideSubtitlePlayerToggleCallbacks: ((hidden: boolean) => void)[];
    private appBarToggleCallbacks: ((hidden: boolean) => void)[];
    private fullscreenToggleCallbacks: ((hidden: boolean) => void)[];
    private subtitleSettingsCallbacks: ((subtitleSettings: SubtitleSettings) => void)[];
    private miscSettingsCallbacks: ((miscSettings: MiscSettings) => void)[];
    private ankiSettingsCallbacks: ((ankiSettings: AnkiSettings) => void)[];
    private alertCallbacks: ((message: string, severity: string) => void)[];
    private copyCallbacks: ((
        postMineAction: PostMineAction,
        subtitle?: SubtitleModel,
        surroundingSubtitles?: SubtitleModel[],
        cardTextFieldValues?: CardTextFieldValues
    ) => void)[];

    constructor(channel: string) {
        this.channel = new BroadcastChannel(channel);
        this.playCallbacks = [];
        this.pauseCallbacks = [];
        this.playbackStateCallbacks = [];
        this.currentTimeCallbacks = [];
        this.audioTrackSelectedCallbacks = [];
        this.closeCallbacks = [];
        this.readyCallbacks = [];
        this.subtitlesCallbacks = [];
        this.subtitlesUpdatedCallbacks = [];
        this.saveTokenLocalCallbacks = [];
        this.offsetCallbacks = [];
        this.playbackRateCallbacks = [];
        this.playModeCallbacks = [];
        this.hideSubtitlePlayerToggleCallbacks = [];
        this.appBarToggleCallbacks = [];
        this.fullscreenToggleCallbacks = [];
        this.subtitleSettingsCallbacks = [];
        this.miscSettingsCallbacks = [];
        this.ankiSettingsCallbacks = [];
        this.alertCallbacks = [];
        this.copyCallbacks = [];

        this.channel.onmessage = (event) => {
            switch (event.data.command) {
                case 'init':
                    // ignore, this is for the chrome extension
                    break;
                case 'ready': {
                    const readyMessage = event.data as ReadyToVideoMessage;

                    for (const callback of this.readyCallbacks) {
                        callback(readyMessage.duration, readyMessage.videoFileName);
                    }
                    break;
                }
                case 'play':
                    for (const callback of this.playCallbacks) {
                        callback();
                    }
                    break;
                case 'pause':
                    for (const callback of this.pauseCallbacks) {
                        callback();
                    }
                    break;
                case 'playbackState': {
                    const playbackStateMessage = event.data as PlaybackStateFromVideoMessage;
                    this.latestPlaybackState = playbackStateMessage;
                    for (const callback of this.playbackStateCallbacks) {
                        callback(playbackStateMessage);
                    }
                    break;
                }
                case 'currentTime': {
                    const currentTimeMessage = event.data as CurrentTimeToVideoMessage;

                    for (const callback of this.currentTimeCallbacks) {
                        callback(currentTimeMessage.value);
                    }
                    break;
                }
                case 'audioTrackSelected': {
                    const audioTrackSelectedMessage = event.data as AudioTrackSelectedToVideoMessage;

                    for (const callback of this.audioTrackSelectedCallbacks) {
                        callback(audioTrackSelectedMessage.id);
                    }
                    break;
                }
                case 'close':
                    for (const callback of this.closeCallbacks) {
                        callback();
                    }
                    break;
                case 'subtitles': {
                    const subtitlesMessage = event.data as SubtitlesToVideoMessage;

                    for (const callback of this.subtitlesCallbacks) {
                        callback(
                            subtitlesMessage.value,
                            subtitlesMessage.names.length > 0 ? subtitlesMessage.names[0] : ''
                        );
                    }
                    break;
                }
                case 'subtitlesUpdated': {
                    const subtitlesUpdatedMessage = event.data as SubtitlesUpdatedToVideoMessage;

                    for (const callback of this.subtitlesUpdatedCallbacks) {
                        callback(subtitlesUpdatedMessage.subtitles);
                    }
                    break;
                }
                case 'saveTokenLocal': {
                    const { track, token, status, states, applyStates } = event.data as SaveTokenLocalToVideoMessage;

                    for (const callback of this.saveTokenLocalCallbacks) {
                        callback(track, token, status, states, applyStates);
                    }
                    break;
                }
                case 'offset': {
                    const offsetMessage = event.data as OffsetToVideoMessage;

                    for (const callback of this.offsetCallbacks) {
                        callback(offsetMessage.value);
                    }
                    break;
                }
                case 'playbackRate': {
                    const playbackRateMessage = event.data as PlaybackRateToVideoMessage;

                    for (const callback of this.playbackRateCallbacks) {
                        callback(playbackRateMessage.value);
                    }
                    break;
                }
                case 'subtitleSettings': {
                    const subtitleSettingsMessage = event.data as SubtitleSettingsToVideoMessage;

                    for (const callback of this.subtitleSettingsCallbacks) {
                        callback(subtitleSettingsMessage.value);
                    }
                    break;
                }
                case 'playMode': {
                    const playModeMessage = event.data as PlayModeMessage;
                    for (const callback of this.playModeCallbacks) {
                        callback(playModeMessage.playMode);
                    }
                    break;
                }
                case 'hideSubtitlePlayerToggle': {
                    const hideSubtitlePlayerToggleMessage = event.data as HideSubtitlePlayerToggleToVideoMessage;

                    for (const callback of this.hideSubtitlePlayerToggleCallbacks) {
                        callback(hideSubtitlePlayerToggleMessage.value);
                    }
                    break;
                }
                case 'appBarToggle': {
                    const appBarToggleMessage = event.data as AppBarToggleMessageToVideoMessage;

                    for (const callback of this.appBarToggleCallbacks) {
                        callback(appBarToggleMessage.value);
                    }
                    break;
                }
                case 'fullscreenToggle': {
                    const fullscreenToggleMessage = event.data as FullscreenToggleMessageToVideoMessage;

                    for (const callback of this.fullscreenToggleCallbacks) {
                        callback(fullscreenToggleMessage.value);
                    }
                    break;
                }
                case 'ankiSettings': {
                    const ankiSettingsMessage = event.data as AnkiSettingsToVideoMessage;

                    for (const callback of this.ankiSettingsCallbacks) {
                        callback(ankiSettingsMessage.value);
                    }
                    break;
                }
                case 'miscSettings': {
                    const miscSettingsMessage = event.data as MiscSettingsToVideoMessage;

                    for (const callback of this.miscSettingsCallbacks) {
                        callback(miscSettingsMessage.value);
                    }
                    break;
                }
                case 'alert': {
                    const alertMessage = event.data as AlertMessage;

                    for (const callback of this.alertCallbacks) {
                        callback(alertMessage.message, alertMessage.severity);
                    }
                    break;
                }
                case 'copy': {
                    const copyMessage = event.data as CopyToVideoMessage;

                    for (const callback of this.copyCallbacks) {
                        const { text, word, definition, customFieldValues } = copyMessage;
                        const cardTextFieldValues = { text, word, definition, customFieldValues };
                        callback(
                            copyMessage.postMineAction,
                            copyMessage.subtitle,
                            copyMessage.surroundingSubtitles,
                            cardTextFieldValues
                        );
                    }
                    break;
                }
                default:
                    asbError('app/messages', 'Unrecognized event ' + event.data.command);
            }
        };
    }

    currentTime(value: number, echo = true) {
        this.channel?.postMessage({ command: 'currentTime', value: value, echo });
    }

    onPlay(callback: () => void) {
        this.playCallbacks.push(callback);
        return () => this._remove(callback, this.playCallbacks);
    }

    onPause(callback: () => void) {
        this.pauseCallbacks.push(callback);
        return () => this._remove(callback, this.pauseCallbacks);
    }

    onPlaybackState(callback: (state: PlaybackStateFromVideoMessage) => void) {
        this.playbackStateCallbacks.push(callback);
        if (this.latestPlaybackState !== undefined) callback(this.latestPlaybackState);
        return () => this._remove(callback, this.playbackStateCallbacks);
    }

    onCurrentTime(callback: (currentTime: number) => void) {
        this.currentTimeCallbacks.push(callback);
        return () => this._remove(callback, this.currentTimeCallbacks);
    }

    onAudioTrackSelected(callback: (id: string) => void) {
        this.audioTrackSelectedCallbacks.push(callback);
        return () => this._remove(callback, this.audioTrackSelectedCallbacks);
    }

    onClose(callback: () => void) {
        this.closeCallbacks.push(callback);
        return () => this._remove(callback, this.closeCallbacks);
    }

    onReady(callback: (duration: number, videoFileName?: string) => void) {
        this.readyCallbacks.push(callback);
        return () => this._remove(callback, this.readyCallbacks);
    }

    onSubtitles(callback: (subtitles: SubtitleModel[], subtitleFileName: string) => void) {
        this.subtitlesCallbacks.push(callback);
        return () => this._remove(callback, this.subtitlesCallbacks);
    }

    onSubtitlesUpdated(callback: (updatedSubtitles: readonly IndexedSubtitleModel[]) => void) {
        this.subtitlesUpdatedCallbacks.push(callback);
        return () => this._remove(callback, this.subtitlesUpdatedCallbacks);
    }

    onSaveTokenLocal(
        callback: (track: number, token: string, status: TokenStatus | null, states: TokenState[]) => void
    ) {
        this.saveTokenLocalCallbacks.push(callback);
        return () => this._remove(callback, this.saveTokenLocalCallbacks);
    }

    onOffset(callback: (offset: number) => void) {
        this.offsetCallbacks.push(callback);
        return () => this._remove(callback, this.offsetCallbacks);
    }

    onPlaybackRate(callback: (playbackRate: number) => void) {
        this.playbackRateCallbacks.push(callback);
        return () => this._remove(callback, this.playbackRateCallbacks);
    }

    onPlayMode(callback: (playMode: PlayMode) => void) {
        this.playModeCallbacks.push(callback);
        return () => this._remove(callback, this.playModeCallbacks);
    }

    onHideSubtitlePlayerToggle(callback: (hidden: boolean) => void) {
        this.hideSubtitlePlayerToggleCallbacks.push(callback);
        return () => this._remove(callback, this.hideSubtitlePlayerToggleCallbacks);
    }

    onAppBarToggle(callback: (hidden: boolean) => void) {
        this.appBarToggleCallbacks.push(callback);
        return () => this._remove(callback, this.appBarToggleCallbacks);
    }

    onFullscreenToggle(callback: (fullscreen: boolean) => void) {
        this.fullscreenToggleCallbacks.push(callback);
        return () => this._remove(callback, this.fullscreenToggleCallbacks);
    }

    onSubtitleSettings(callback: (subtitleSettings: SubtitleSettings) => void) {
        this.subtitleSettingsCallbacks.push(callback);
        return () => this._remove(callback, this.subtitleSettingsCallbacks);
    }

    onMiscSettings(callback: (miscSettings: MiscSettings) => void) {
        this.miscSettingsCallbacks.push(callback);
        return () => this._remove(callback, this.miscSettingsCallbacks);
    }

    onAnkiSettings(callback: (ankiSettings: AnkiSettings) => void) {
        this.ankiSettingsCallbacks.push(callback);
        return () => this._remove(callback, this.ankiSettingsCallbacks);
    }

    onAlert(callback: (message: string, severity: string) => void) {
        this.alertCallbacks.push(callback);
        return () => this._remove(callback, this.alertCallbacks);
    }

    onCopy(
        callback: (
            postMineAction: PostMineAction,
            subtitle?: SubtitleModel,
            surroundingSubtitles?: SubtitleModel[],
            cardTextFieldValues?: CardTextFieldValues
        ) => void
    ) {
        this.copyCallbacks.push(callback);
        return () => this._remove(callback, this.copyCallbacks);
    }

    ready(
        duration: number,
        paused: boolean,
        playbackRate: number,
        audioTracks: AudioTrackModel[] | undefined,
        selectedAudioTrack: string | undefined
    ) {
        const message: ReadyFromVideoMessage = {
            command: 'ready',
            duration: duration,
            paused: paused,
            currentTime: 0,
            audioTracks: audioTracks,
            selectedAudioTrack: selectedAudioTrack,
            playbackRate: playbackRate,
        };

        this.channel?.postMessage(message);
    }

    duration(duration: number) {
        const message: DurationFromVideoMessage = { command: 'duration', value: duration };
        this.channel?.postMessage(message);
    }

    readyState(readyState: number) {
        const message: ReadyStateFromVideoMessage = { command: 'readyState', value: readyState };
        this.channel?.postMessage(message);
    }

    play(echo = true) {
        const message: PlayFromVideoMessage = { command: 'play', echo };
        this.channel?.postMessage(message);
    }

    pause(echo = true) {
        const message: PauseFromVideoMessage = { command: 'pause', echo };
        this.channel?.postMessage(message);
    }

    playbackState(state: PlaybackState) {
        const message: PlaybackStateFromVideoMessage = {
            command: 'playbackState',
            ...state,
        };
        this.channel?.postMessage(message);
    }

    audioTrackSelected(id: string) {
        const message: AudioTrackSelectedFromVideoMessage = { command: 'audioTrackSelected', id: id };
        this.channel?.postMessage(message);
    }

    offset(offset: number) {
        const message: OffsetFromVideoMessage = { command: 'offset', value: offset };
        this.channel?.postMessage(message);
    }

    playbackRate(playbackRate: number, echo = true) {
        const message: PlaybackRateFromVideoMessage = { command: 'playbackRate', value: playbackRate, echo };
        this.channel?.postMessage(message);
    }

    saveTokenLocal(
        track: number,
        token: string,
        status: TokenStatus | null,
        states: TokenState[],
        applyStates: ApplyStrategy
    ) {
        const message: SaveTokenLocalFromVideoMessage = {
            command: 'saveTokenLocal',
            track,
            token,
            status,
            states,
            applyStates,
        };
        this.channel?.postMessage(message);
    }

    popOutToggle() {
        this.channel?.postMessage({ command: 'popOutToggle' });
    }

    copy(
        subtitle: SubtitleModel,
        surroundingSubtitles: SubtitleModel[],
        cardTextFieldValues: CardTextFieldValues,
        subtitleFileName: string,
        mediaTimestamp: number,
        postMineAction: PostMineAction
    ) {
        const message: CopyMessage = {
            command: 'copy',
            subtitle,
            surroundingSubtitles,
            ...cardTextFieldValues,
            subtitleFileName,
            postMineAction,
            mediaTimestamp,
        };

        this.channel?.postMessage(message);
    }

    playModes(playModes: Set<PlayMode>) {
        const message: PlayModesMessage = { command: 'playModes', playModes: [...playModes] };
        this.channel?.postMessage(message);
    }

    hideSubtitlePlayerToggle() {
        this.channel?.postMessage({ command: 'hideSubtitlePlayerToggle' });
    }

    appBarToggle() {
        this.channel?.postMessage({ command: 'appBarToggle' });
    }

    toggleSubtitleTrackInList(track: number) {
        const message: ToggleSubtitleTrackInListFromVideoMessage = {
            command: 'toggleSubtitleTrackInList',
            track: track,
        };
        this.channel?.postMessage(message);
    }

    subtitlesUpdated(updatedSubtitles: readonly IndexedSubtitleModel[]) {
        const message: SubtitlesUpdatedFromVideoMessage = {
            command: 'subtitlesUpdated',
            updatedSubtitles,
        };
        this.channel?.postMessage(message);
    }

    tokenSelectionFocus = () => {
        this.channel?.postMessage({ command: 'tokenSelectionFocus' });
    };

    loadFiles() {
        this.channel?.postMessage({ command: 'loadFiles' });
    }

    loadSubtitles() {
        this.channel?.postMessage({ command: 'loadSubtitles' });
    }

    close() {
        if (this.channel) {
            this.channel.postMessage({ command: 'exit' });
            this.channel.close();
            this.channel = undefined;
            this.playCallbacks = [];
            this.pauseCallbacks = [];
            this.playbackStateCallbacks = [];
            this.latestPlaybackState = undefined;
            this.currentTimeCallbacks = [];
            this.audioTrackSelectedCallbacks = [];
            this.closeCallbacks = [];
            this.readyCallbacks = [];
            this.subtitlesCallbacks = [];
            this.subtitlesUpdatedCallbacks = [];
            this.offsetCallbacks = [];
            this.playbackRateCallbacks = [];
            this.playModeCallbacks = [];
            this.hideSubtitlePlayerToggleCallbacks = [];
            this.appBarToggleCallbacks = [];
            this.fullscreenToggleCallbacks = [];
            this.subtitleSettingsCallbacks = [];
            this.miscSettingsCallbacks = [];
            this.ankiSettingsCallbacks = [];
            this.alertCallbacks = [];
            this.copyCallbacks = [];
        }
    }

    _remove<T>(callback: T, callbacks: T[]) {
        for (let i = callbacks.length - 1; i >= 0; --i) {
            if (callback === callbacks[i]) {
                callbacks.splice(i, 1);
                break;
            }
        }
    }
}
